/**
 * @module components/journey
 *
 * The phase deep-dive carousel: nine panels in a native horizontal scrollport
 * with `scroll-snap`, an entrance choreography and a progress ladder that
 * doubles as navigation.
 *
 * ## Why a native scrollport and not a pinned timeline
 *
 * A pinned, `x`-scrubbed GSAP timeline looks impressive but behaves badly inside
 * a transformed smooth-scroll container (the pin offset depends on when the
 * smoother last measured the page) and it removes keyboard scrolling, trackpad
 * momentum and scrollbar semantics. `overflow-x` with
 * `scroll-snap-type: x proximity` gives all of those for free.
 *
 * Snapping is `proximity`, not `mandatory`: mandatory snapping cancels
 * programmatic `scrollTo()` calls and can strand the track between panels.
 *
 * On small screens the scrollport is disabled and the panels stack vertically
 * (see `components.css`), so the content is always readable.
 */

import { gsap, ScrollTrigger, MEDIA, createMotionContext } from "../core/gsap-setup.js";
import { qs, qsa, on } from "../core/dom.js";
import { watchReveal } from "../animations/scrollReveal.js";
import { transformationRiver } from "../svg/scenes.js";
import { PHASES } from "../data/phases.js";
import { icon } from "../svg/icons.js";

/**
 * Build one journey panel.
 *
 * @param {import("../data/phases.js").Phase} phase
 * @returns {string} Markup.
 */
function panelMarkup(phase) {
  const list = (items) => items.map((item) => `<li>${item}</li>`).join("");

  return `
    <article class="journey-panel" data-journey-panel="${phase.id}"
             style="--panel-accent:${phase.accent}"
             aria-label="Phase ${phase.code}: ${phase.name}">
      <p class="journey-panel__step">Phase ${phase.code} · ${phase.index + 1} of ${PHASES.length}</p>
      <h3 class="journey-panel__title">${phase.name}</h3>
      <p class="journey-panel__tagline">${phase.tagline}</p>
      <p class="journey-panel__focus">${phase.focus}</p>
      <p class="journey-panel__body">${phase.narrative}</p>

      <div class="journey-panel__cols">
        <div class="phase-list">
          <p class="phase-list__title">Activities</p>
          <ul>${list(phase.activities)}</ul>
        </div>
        <div class="phase-list">
          <p class="phase-list__title">Deliverables</p>
          <ul>${list(phase.deliverables)}</ul>
        </div>
      </div>

      <div class="phase-panel__gate">
        ${icon("check", { size: 18 })}
        <div>
          <strong>Quality gate</strong>
          ${phase.qualityGate}
        </div>
      </div>

      <div class="journey-panel__ai">
        <p class="journey-panel__ai-label">${icon("chip", { size: 14 })} AI acceleration</p>
        <p class="journey-panel__ai-text"><strong>${phase.ai.headline}</strong> ${phase.ai.body}</p>
      </div>
    </article>`;
}

/**
 * @returns {() => void} Teardown.
 */
export function initJourney() {
  const root = qs("[data-journey]");
  if (!root) return () => {};

  const stage = qs("[data-journey-stage]", root);
  const ladder = qs("[data-journey-ladder]", root);
  const count = qs("[data-journey-count]", root);
  if (!stage) return () => {};

  /* --- Render ------------------------------------------------------------ */

  const backdrop = qs("[data-journey-backdrop]", root);
  if (backdrop) {
    backdrop.innerHTML = transformationRiver({ lanes: PHASES.length, accent: "#5fd4c4", secondary: "#d8a657" });
  }

  stage.innerHTML = PHASES.map(panelMarkup).join("");

  if (ladder) {
    ladder.innerHTML = PHASES.map(
      (phase, index) => `
        <button type="button" data-journey-jump="${index}"
                aria-current="${index === 0}"
                aria-label="Go to phase ${phase.code}: ${phase.name}"
                title="Phase ${phase.code} · ${phase.name}"></button>`
    ).join("");
  }

  const panels = qsa("[data-journey-panel]", stage);
  const jumps = qsa("[data-journey-jump]", ladder || root);

  const setCount = (index) => {
    if (count) {
      count.textContent = `${String(index + 1).padStart(2, "0")} / ${String(PHASES.length).padStart(2, "0")}`;
    }
    jumps.forEach((jump, i) => jump.setAttribute("aria-current", String(i === index)));
  };
  setCount(0);

  const disposers = [];

  /* --- Navigation -------------------------------------------------------- */

  const goTo = (index) => {
    const panel = panels[index];
    if (!panel) return;
    stage.scrollTo({ left: panel.offsetLeft - stage.offsetLeft, behavior: "smooth" });
  };

  jumps.forEach((jump, index) => {
    disposers.push(on(jump, "click", () => goTo(index)));
  });

  /* --- Active panel tracking --------------------------------------------- */

  const updateActive = () => {
    if (!panels.length) return;
    const centre = stage.scrollLeft + stage.clientWidth / 2;
    let nearest = 0;
    let best = Infinity;

    panels.forEach((panel, index) => {
      const panelCentre = panel.offsetLeft - stage.offsetLeft + panel.offsetWidth / 2;
      const distance = Math.abs(panelCentre - centre);
      if (distance < best) {
        best = distance;
        nearest = index;
      }
    });

    setCount(nearest);
  };

  disposers.push(on(stage, "scroll", updateActive, { passive: true }));

  // Keyboard support for the scrollport itself, in addition to native keys.
  disposers.push(
    on(stage, "keydown", (event) => {
      if (!["ArrowRight", "ArrowLeft", "Home", "End"].includes(event.key)) return;
      const centre = stage.scrollLeft + stage.clientWidth / 2;
      let nearest = 0;
      let best = Infinity;
      panels.forEach((panel, index) => {
        const panelCentre = panel.offsetLeft - stage.offsetLeft + panel.offsetWidth / 2;
        const distance = Math.abs(panelCentre - centre);
        if (distance < best) {
          best = distance;
          nearest = index;
        }
      });

      if (event.key === "ArrowRight") goTo(Math.min(nearest + 1, panels.length - 1));
      else if (event.key === "ArrowLeft") goTo(Math.max(nearest - 1, 0));
      else if (event.key === "Home") goTo(0);
      else goTo(panels.length - 1);

      event.preventDefault();
    })
  );

  /* --- Entrance choreography --------------------------------------------- */

  const mm = createMotionContext();

  mm.add(MEDIA.reduce, () => {
    gsap.set(panels, { clearProps: "all", autoAlpha: 1 });
  });

  mm.add(MEDIA.any, () => {
    const disposeReveal = watchReveal(panels, {
      from: { autoAlpha: 0, y: 30 },
      to: { autoAlpha: 1, y: 0 },
      duration: 0.85,
      stagger: 0.08,
      start: "top 78%",
    });

    // Keep the ladder in sync when the section scrolls into view.
    const trigger = ScrollTrigger.create({
      trigger: root,
      start: "top 85%",
      end: "bottom top",
      onToggle: (self) => {
        if (self.isActive) updateActive();
      },
    });

    return () => {
      disposeReveal();
      trigger.kill();
      gsap.set(panels, { clearProps: "all" });
    };
  });

  return () => {
    disposers.forEach((dispose) => dispose());
    mm.revert();
  };
}
